import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { JourneyStatus, JourneySummary } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import { listJourneys } from "@/lib/journeys-api";

// TES-224 — clinician/coach journey surface. A journey groups flows; lists each
// journey with its member count, status and version. The builder lives at
// /journeys/:key.

const STATUS_LABELS: Record<JourneyStatus, string> = {
  draft: "Utkast",
  published: "Publicerad",
  archived: "Arkiverad",
};

function statusChip(status: JourneyStatus) {
  const cls =
    status === "published"
      ? "bg-success/10 text-success border-success/30"
      : status === "archived"
        ? "bg-surfaceMuted text-textSecondary border-surfaceMuted"
        : "bg-warning/10 text-warning border-warning/30";
  return (
    <span
      className={`inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border ${cls}`}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

export function JourneysListRoute() {
  const { signOut } = useAuth();
  const [rows, setRows] = useState<JourneySummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    listJourneys()
      .then(setRows)
      .catch((e) => {
        if (e instanceof UnauthorizedError) {
          void signOut();
          return;
        }
        setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => setLoading(false));
  }, [signOut]);

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">Resor</h1>
        <Link
          to="/journeys/new"
          className="rounded-md bg-brand text-white px-md py-sm text-sm font-medium hover:bg-brand/90 transition"
        >
          Ny resa
        </Link>
      </div>

      <p className="text-sm text-textSecondary max-w-2xl">
        En resa grupperar flera flöden. En patient kan kopplas till flera resor
        samtidigt; varje flöde i resan startar efter sin egen trigger (manuellt
        eller av en händelse). Ändringar börjar gälla först när du publicerar.
      </p>

      <div className="flex items-end">
        <span className="text-sm text-textSecondary ml-auto">
          {loading ? "Hämtar…" : rows ? `${rows.length} resor` : ""}
        </span>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}

      {rows && rows.length === 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md p-md text-textSecondary">
          Inga resor ännu. Skapa din första med “Ny resa”.
        </div>
      ) : null}

      {rows && rows.length > 0 ? (
        <div className="bg-surface border border-surfaceMuted rounded-md overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surfaceMuted/60 text-textSecondary uppercase tracking-wide text-xs">
              <tr>
                <th className="text-left px-md py-sm font-medium">Namn</th>
                <th className="text-left px-md py-sm font-medium">Nyckel</th>
                <th className="text-left px-md py-sm font-medium">Status</th>
                <th className="text-left px-md py-sm font-medium">Version</th>
                <th className="text-left px-md py-sm font-medium">Flöden</th>
                <th className="px-md py-sm" aria-label="actions" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.key}
                  className="border-t border-surfaceMuted hover:bg-surfaceMuted/40 transition"
                >
                  <td className="px-md py-sm font-medium">
                    {row.name || "(utan namn)"}
                  </td>
                  <td className="px-md py-sm font-mono text-xs text-textSecondary">
                    {row.key}
                  </td>
                  <td className="px-md py-sm">{statusChip(row.status)}</td>
                  <td className="px-md py-sm text-textSecondary">
                    v{row.version}
                  </td>
                  <td className="px-md py-sm text-textSecondary">
                    {row.memberCount}
                  </td>
                  <td className="px-md py-sm text-right">
                    <Link
                      to={`/journeys/${encodeURIComponent(row.key)}`}
                      className="text-brand hover:underline text-sm font-medium"
                    >
                      Redigera →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
